import { useEffect, useState } from "react";
import type { EqmsController } from "../../../controllers/useEqms";
import * as eqmsApi from "../../../controllers/eqmsApi";
import { friendlyMessage } from "../../../controllers/eqmsApi";
import { listSprints } from "../../../controllers/projectApi";
import type { Version, VersionStatus } from "../../../models/eqms";
import { VERSION_STATUS_LABEL, WORK_AREA_LABEL } from "../../../models/eqms";
import type { Sprint } from "../../../models/project";

const SPRINT_STATUS_LABEL: Record<Sprint["status"], string> = {
  FUTURE: "예정", ACTIVE: "진행 중", CLOSED: "종료",
};

const RELEASABLE: VersionStatus[] = ["PLANNED", "IN_DEV"];

// The eQMS module's development-cycle screen: sprints (project-level) cross-referenced with the
// version roadmap, plus the "release decision" action that ships a version at the end of an active
// sprint (eqmsApi.releaseVersion records which sprint released which version).
export function EqmsSprintView({ eqms }: { eqms: EqmsController }) {
  const [sprints, setSprints] = useState<Sprint[]>([]);
  const [versions, setVersions] = useState<Version[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedVersionBySprint, setSelectedVersionBySprint] = useState<Record<string, string>>({});
  const [releasingSprintId, setReleasingSprintId] = useState<string | null>(null);

  const load = () => {
    if (!eqms.projectId) { setSprints([]); setVersions([]); setLoading(false); return; }
    setLoading(true);
    setError(null);
    Promise.all([
      listSprints(eqms.getToken, eqms.projectId),
      eqmsApi.listVersions(eqms.getToken, eqms.projectId),
    ])
      .then(([sprintList, versionList]) => {
        setSprints(sprintList);
        setVersions(versionList);
      })
      .catch((e) => setError(friendlyMessage(e)))
      .finally(() => setLoading(false));
  };

  useEffect(load, [eqms.getToken, eqms.projectId]);

  const releasableVersions = versions.filter((v) => RELEASABLE.includes(v.status));

  async function decideRelease(sprintId: string) {
    const versionId = selectedVersionBySprint[sprintId];
    if (!versionId) return;
    setReleasingSprintId(sprintId);
    try {
      await eqmsApi.releaseVersion(eqms.getToken, versionId, sprintId);
      load();
    } catch (e) {
      setError(friendlyMessage(e));
    } finally {
      setReleasingSprintId(null);
    }
  }

  return (
    <div>
      <div style={{ marginBottom: 16 }}>
        <h2 style={{ margin: 0 }}>스프린트</h2>
        <div className="note" style={{ marginTop: 4 }}>
          개발 주기 · 배포 결정{eqms.workArea && <> — <span className="tag">{WORK_AREA_LABEL[eqms.workArea]}</span> (범위 참고용, 스프린트는 프로젝트 단위입니다)</>}
        </div>
      </div>

      {error && (
        <div className="note" style={{ background: "#fdeef0", marginBottom: 16 }}>
          <b style={{ color: "var(--amber)" }}>오류:</b> {error}
        </div>
      )}

      {!eqms.projectId ? (
        <div className="center">프로젝트를 선택하세요.</div>
      ) : loading ? (
        <div className="center">불러오는 중…</div>
      ) : sprints.length === 0 ? (
        <div className="sdui-list-empty">스프린트가 없습니다.</div>
      ) : (
        <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
          {sprints.map((sprint) => {
            const releasedVersion = versions.find((v) => v.releasedInSprintId === sprint.id);
            return (
              <div className="info-card" key={sprint.id}>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
                  <div>
                    <h4 style={{ margin: 0 }}>{sprint.name}</h4>
                    <div style={{ marginTop: 4 }}>
                      <span className="tag">{SPRINT_STATUS_LABEL[sprint.status]}</span>
                      {sprint.startDate && sprint.endDate && (
                        <span className="note" style={{ marginLeft: 8 }}>{sprint.startDate.slice(0, 10)} ~ {sprint.endDate.slice(0, 10)}</span>
                      )}
                    </div>
                  </div>
                </div>

                {releasedVersion ? (
                  <div className="note" style={{ marginTop: 10 }}>
                    배포된 버전: <b>{releasedVersion.label}</b> ({VERSION_STATUS_LABEL[releasedVersion.status]})
                  </div>
                ) : sprint.status === "ACTIVE" ? (
                  <div style={{ display: "flex", gap: 8, alignItems: "center", marginTop: 10, flexWrap: "wrap" }}>
                    <select
                      className="sdui-input"
                      style={{ width: "auto" }}
                      value={selectedVersionBySprint[sprint.id] ?? ""}
                      onChange={(e) =>
                        setSelectedVersionBySprint((m) => ({ ...m, [sprint.id]: e.target.value }))
                      }
                    >
                      <option value="">버전 선택…</option>
                      {releasableVersions.map((v) => (
                        <option key={v.id} value={v.id}>
                          {v.label} ({VERSION_STATUS_LABEL[v.status]})
                        </option>
                      ))}
                    </select>
                    <button
                      className="btn primary"
                      style={{ width: "auto" }}
                      disabled={!selectedVersionBySprint[sprint.id] || releasingSprintId === sprint.id}
                      onClick={() => decideRelease(sprint.id)}
                    >
                      {releasingSprintId === sprint.id ? "처리 중…" : "배포 결정"}
                    </button>
                  </div>
                ) : (
                  <div className="note" style={{ marginTop: 10 }}>이 스프린트에서 배포된 버전이 없습니다.</div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
